import type { AppContext } from "../app/app";
import type { Dimension, Valence } from "../data/model";
import { el } from "./dom";

const dateStr = (ms: number) => new Date(ms).toISOString().slice(0, 10);

function group(label: string, control: HTMLElement): HTMLElement {
  return el(
    "div",
    { class: "inst__group" },
    el("span", { class: "label inst__group-label", text: label }),
    control,
  );
}

// The "Log event" form: one dated +/- event of some significance. It is not a
// rating of the person; it nudges the standing.
export function renderEventForm(
  ctx: AppContext,
  personId: string,
  dim: Dimension,
  onDone: () => void,
): HTMLElement {
  let valence: Valence = "+";
  const pos = el("button", { class: "inst__val is-on", type: "button", text: "Positive" });
  const neg = el("button", { class: "inst__val", type: "button", text: "Negative" });
  const setVal = (v: Valence) => {
    valence = v;
    pos.classList.toggle("is-on", v === "+");
    neg.classList.toggle("is-on", v === "-");
  };
  pos.addEventListener("click", () => setVal("+"));
  neg.addEventListener("click", () => setVal("-"));

  let magnitude = 2;
  const magBtns: HTMLButtonElement[] = [];
  const mags = el("div", { class: "inst__mags" });
  ["Minor", "Moderate", "Major"].forEach((label, i) => {
    const n = i + 1;
    const b = el("button", {
      class: `inst__mag${n === magnitude ? " is-on" : ""}`,
      type: "button",
      text: label,
      on: {
        click: () => {
          magnitude = n;
          magBtns.forEach((x, j) => x.classList.toggle("is-on", j + 1 === magnitude));
        },
      },
    });
    magBtns.push(b);
    mags.append(b);
  });

  const note = el("input", { class: "input inst__note-in", type: "text", placeholder: "What happened?" });
  const date = el("input", { class: "input inst__date-in", type: "date", value: dateStr(Date.now()) });
  const log = el("button", {
    class: "btn btn--primary",
    text: "Log event",
    on: {
      click: async () => {
        const occurred_at = date.value ? Date.parse(date.value) : Date.now();
        await ctx.repo.addInstance({
          person_id: personId,
          dimension_id: dim.id,
          valence,
          magnitude,
          note: note.value.trim(),
          occurred_at,
        });
        onDone();
      },
    },
  });

  return el(
    "div",
    { class: "inst__form" },
    el("p", {
      class: "inst__form-intro",
      text:
        `A dated event on ${dim.name} — something they did, good or bad. It ` +
        "nudges the standing; bigger events move it more. It is not a rating.",
    }),
    el(
      "div",
      { class: "inst__form-row" },
      group("This event was", el("span", { class: "inst__valgroup" }, pos, neg)),
      group("Significance", mags),
      group("When", date),
    ),
    el("div", { class: "inst__form-row" }, note, log),
    el("p", {
      class: "sensitive-note",
      text: "Do not record sensitive personal data (health, politics, religion, sexuality, etc.).",
    }),
  );
}
